import { Children, cloneElement, isValidElement, ReactNode, useRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/classes";
import { CustomDropdownItem } from "./custom-dropdown-item";

/* Variants */
const variants = cva("dropdown", {
  variants: {
    position: {
      top: "dropdown-top",
      left: "dropdown-left",
      bottom: "dropdown-bottom",
      right: "dropdown-right",
    },
    align: {
      start: "dropdown-start",
      end: "dropdown-end",
    },
  },
});

/* Prop Types */
interface DropdownProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof variants> {
  children: ReactNode;
  triggerBtn?: ReactNode;
  triggerBtnClassname?: string;
  dropdownClassname?: string;
  listClassname?: string;
  header?: ReactNode;
}

/*
 * Custom Dropdown Component
 * This component is a custom dropdown component that allows you to pass custom trigger button and dropdown list.
 */
export const CustomDropdown = ({ position, align, children, triggerBtn, triggerBtnClassname, dropdownClassname, listClassname, header, ...htmlAttributes }: DropdownProps) => {
  /* Refs */
  const dropdownRef = useRef<HTMLUListElement>(null);

  /* Render */
  return (
    <>
      <div className={cn(variants({ position, align }), dropdownClassname)} onClick={(e) => e.stopPropagation()}>
        <div
          tabIndex={0}
          className={cn("cursor-pointer py-2 hover:bg-base-200 rounded-md focus:bg-base-200", triggerBtnClassname)}
          onClick={(e) => {
            e.stopPropagation();
          }}
          {...htmlAttributes}
        >
          {triggerBtn ? triggerBtn : <EllipsisVerticalIcon className="h-6 w-6 text-gray-500" />}
        </div>
        <ul
          tabIndex={0}
          className={cn("dropdown-content menu bg-base-100 rounded-2xl z-50 w-52 px-2 py-3 shadow-lg drop-shadow-lg  max-h-[50vh] overflow-auto flex-row", listClassname)}
          ref={dropdownRef}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {header && header}
          {Children.map(children, (child) => {
            if (isValidElement(child)) {
              /* Check if the child is a CustomDropdownItem by comparing with the actual component */
              const isCustomDropdownItem = child.type === CustomDropdownItem;

              if (isCustomDropdownItem) {
                const childProps = typeof child.props === "object" && child.props !== null ? child.props : {};
                return cloneElement(child, {
                  ...childProps,
                  dropdownRef,
                } as React.HTMLAttributes<HTMLElement> & { dropdownRef: React.RefObject<HTMLUListElement> });
              }
            }
            return child;
          })}
        </ul>
      </div>
    </>
  );
};
